import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const ToastContext = createContext<(msg: string) => void>(() => {});

// Kurze Meldung unten am Bildschirm, verschwindet nach ein paar Sekunden.
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const host = useOpenModal();
  const show = useCallback((m: string) => {
    setMsg(m);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 3500);
  }, []);
  const region = (
    <div role="status" aria-live="polite" className={msg ? 'toast' : 'sr-only'}>
      {msg}
    </div>
  );
  return (
    <ToastContext.Provider value={show}>
      {children}
      {host ? createPortal(region, host) : region}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// Ein offenes modales Fenster (<dialog> mit showModal) liegt über allem anderen, auch über jedem z-index,
// und blendet den Rest der Seite für Screenreader aus. Solange eins offen ist, steht die Meldung darin,
// damit man sie sieht und sie vorgelesen wird.
function useOpenModal(): HTMLDialogElement | null {
  const [host, setHost] = useState<HTMLDialogElement | null>(null);
  useEffect(() => {
    const update = () => setHost(topModal());
    const watch = new MutationObserver(update);
    watch.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
    update();
    return () => watch.disconnect();
  }, []);
  return host;
}

function topModal(): HTMLDialogElement | null {
  const open = [...document.querySelectorAll('dialog[open]')].filter(isModal);
  return (open[open.length - 1] as HTMLDialogElement | undefined) ?? null;
}

function isModal(d: Element): boolean {
  try {
    return d.matches(':modal');
  } catch {
    // ältere Browser ohne :modal; hier werden Fenster ohnehin nur modal geöffnet
    return true;
  }
}
